import { useMemo, useState, useCallback, useEffect } from 'react'

import { useProduct } from '../../index.js'

import { useI18n } from '../../../../shared/i18n/useI18n.js'
import { useProductStrings } from '../../../../shared/i18n/useProductStrings.js'

import { formatCurrency } from '../../../../shared/utils/priceUtils.js'
import { formatProductArea } from '../../../../shared/utils/productFormatters.js'

import { buildPriceChartData } from '../../utils/priceChartUtils.js'
import { PriceHistoryChartSvg } from './PriceHistoryChartSvg.jsx'

import { Button }       from '../../../../shared/ui/Button/Button.jsx'
import { ButtonGroup }  from '../../../../shared/ui/Button/ButtonGroup.jsx'
import { History }      from '../../../../shared/ui/icons/History.jsx'
import { Modal }        from '../../../../shared/ui/Modal/index.js'
import { OfferBadge }   from '../../../../shared/ui/OfferBadge/OfferBadge.jsx'
import { RangeSlider }  from '../../../../shared/ui/RangeSlider/index.js'

import styles from './PriceHistoryModal.module.scss'

const CHART_KEYS = {
  PRICE_TOTAL: 'price_brutto',
  PRICE_M2: 'price_m2_brutto',
}

/**
 * PriceHistoryModal — displays product price history in tabular and chart forms.
 *
 * Data source: `product.price_history = [{ date: string, price_brutto: number, price_m2_brutto?: number, area?: number }]`
 *
 * Features:
 *   - Table view: date | price_brutto | price_m2_brutto (newest first)
 *   - Chart view: step chart (SVG, horizontal lines with steps) + RangeSlider date filter
 *   - Fallback for price_m2_brutto: `price_brutto / area` or `price_brutto / product.area_original`
 *   - Disclaimer: conditional text based on first price entry date
 *   - Date range slider: min = first entry date, max = today
 *
 * @param {object}       props
 * @param {boolean}      props.isOpen
 * @param {() => void}   props.onClose
 * @param {object}       props.pricing
 */
export function PriceHistoryModal({ isMobile, isOpen, onClose, pricing }) {
  // ── i18n ──────────────────────────────────────────────────────────────────
  const { t } = useI18n()
  const getProductCategoryString = useProductStrings()

  // ── Context reads ─────────────────────────────────────────────────────────
  const { selection } = useProduct()
  const product = selection.product
  const {
    isOnOffer,
    pricePerSqm,
    strikethroughPricePerSqm,
    omnibusPricePerSqm,
    totalPrice,
    strikethroughPrice,
    omnibusPrice,
    vatNote,
    currency,
    locale,
  } = pricing

  // ── Local state ───────────────────────────────────────────────────────────
  const [showChart, setShowChart] = useState(false)
  const [priceKey, setPriceKey] = useState(CHART_KEYS.PRICE_TOTAL)
  const [dateRangeMs, setDateRangeMs] = useState([null, null])
  const [rangeUpperBoundMs, setRangeUpperBoundMs] = useState(() => Date.now())
  const [hoveredPointIndex, setHoveredPointIndex] = useState(null)

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return
    setRangeUpperBoundMs(Date.now())
    setHoveredPointIndex(null)
    setDateRangeMs([null, null])
  }, [isOpen])

  // ── Derived state ─────────────────────────────────────────────────────────
  // Normalize price history — compute price_m2_brutto fallback
  const normalizedHistory = useMemo(() => {
    if (!product?.price_history || product.price_history.length === 0) {
      return []
    }

    return product.price_history
      .map((entry) => {
        const timestamp = Date.parse(entry.date)
        if (!Number.isFinite(timestamp)) return null

        const brutto = Number(entry.price_brutto)
        if (!Number.isFinite(brutto) || brutto <= 0) return null

        const area = Number(entry.area ?? product.area_original)
        const directM2 = Number(entry.price_m2_brutto)
        const price_m2_brutto =
          Number.isFinite(directM2) && directM2 > 0
            ? directM2
            : Number.isFinite(area) && area > 0
              ? brutto / area
              : null

        return {
          date: entry.date,
          timestamp,
          price_brutto: brutto,
          price_m2_brutto,
        }
      })
      .filter(Boolean)
      .sort((a, b) => a.timestamp - b.timestamp)
  }, [product?.price_history, product?.area_original])

  // Date range bounds: min = first entry date, max = moment modal was opened
  const dateRangeBounds = useMemo(() => {
    if (normalizedHistory.length === 0) return [rangeUpperBoundMs, rangeUpperBoundMs]
    const minMs = normalizedHistory[0].timestamp
    const maxMs = rangeUpperBoundMs
    return [minMs, maxMs]
  }, [normalizedHistory, rangeUpperBoundMs])

  // Clamp user-selected date range to valid bounds; initialize as full range if not set
  const effectiveDateRange = useMemo(() => {
    const [minMs, maxMs] = dateRangeBounds
    const [userMinMs, userMaxMs] = dateRangeMs

    if (userMinMs === null || userMaxMs === null) {
      return [minMs, maxMs]
    }

    const clampedMin = Math.max(minMs, Math.min(userMinMs, maxMs))
    const clampedMax = Math.max(minMs, Math.min(userMaxMs, maxMs))

    return clampedMin <= clampedMax ? [clampedMin, clampedMax] : [clampedMax, clampedMin]
  }, [dateRangeBounds, dateRangeMs])

  // History sorted newest-first for table display
  const tableHistory = useMemo(() => [...normalizedHistory].reverse(), [normalizedHistory])

  // Helper: format date to locale string
  const formatDate = useCallback((dateString) => {
    if (!dateString) return '—'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    } catch {
      return dateString
    }
  }, [])

  // Disclaimer: if first price entry (oldest) is recent, show "w sprzedaży od..."
  const disclaimer = useMemo(() => {
    if (!normalizedHistory || normalizedHistory.length === 0) return null

    const oldestEntry = normalizedHistory[0]
    const dateThreshold = new Date('2025-09-11') // arbitrary threshold per plan
    const entryDate = new Date(oldestEntry.date)

    if (entryDate >= dateThreshold) {
      const formattedDate = formatDate(oldestEntry.date)
      return getProductCategoryString('price_history_start_date', product.product_category, { date: formattedDate })
    }

    return getProductCategoryString('price_history_default_date', product.product_category)
  }, [normalizedHistory, formatDate, getProductCategoryString, product.product_category])

  // Build chart data from all entries but mapped to current selected date range.
  const chartData = useMemo(() => {
    if (normalizedHistory.length === 0) return null
    return buildPriceChartData({
      entries: normalizedHistory,
      rangeStart: effectiveDateRange[0],
      rangeEnd: effectiveDateRange[1],
      priceKey,
    })
  }, [normalizedHistory, effectiveDateRange, priceKey])

  // Determine if range slider is in active state (not at maximum range)
  const isRangeActive = useMemo(() => {
    const [minMs, maxMs] = dateRangeBounds
    const [effMinMs, effMaxMs] = effectiveDateRange
    return effMinMs > minMs || effMaxMs < maxMs
  }, [dateRangeBounds, effectiveDateRange])

  // ── Callbacks ─────────────────────────────────────────────────────────────
  const handleDateRangeChange = useCallback((newDateRange) => {
    const [boundMin, boundMax] = dateRangeBounds
    const loRaw = Number(newDateRange?.[0])
    const hiRaw = Number(newDateRange?.[1])

    const lo = Number.isFinite(loRaw)
      ? Math.max(boundMin, Math.min(boundMax, loRaw))
      : boundMin
    const hi = Number.isFinite(hiRaw)
      ? Math.max(boundMin, Math.min(boundMax, hiRaw))
      : boundMax

    const nextRange = lo <= hi ? [lo, hi] : [hi, lo]
    const isFullRange = nextRange[0] === boundMin && nextRange[1] === boundMax
    setDateRangeMs(isFullRange ? [null, null] : nextRange)
    setHoveredPointIndex(null)
  }, [dateRangeBounds])

  const handleHoveredPointChange = useCallback((nextIndex) => {
    setHoveredPointIndex(nextIndex)
  }, [])

  const formatSliderValue = useCallback((dateMs) => {
    if (!dateMs) return '—'
    return formatDate(new Date(dateMs).toISOString().split('T')[0])
  }, [formatDate])

  const headingIconSize = isMobile ? '2em' : '1.2em'
  const togglesSize = isMobile ? 'small' : 'medium'
  const formatTogglesSize = '6em'
  const chartTogglesSize = '10em'

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={getProductCategoryString('price_history', product.product_category, { id: product.id_original })}
    >
      <Modal.Header onClose={onClose} closeLabel={t('common.close')}>
        <div className={styles.headingContainer}>
          <div className={styles.headingBox}>
            <span className={styles.heading}>
              <History 
                width={headingIconSize} 
                height={headingIconSize}
              />
              {getProductCategoryString('price_history', product.product_category, { id: product.id_original })}
            </span>
            <span className={styles.subHeading}>
              {normalizedHistory?.length > 1 && t('product.price_history_last_change', {date: formatDate(normalizedHistory?.[normalizedHistory.length - 1]?.date)})}
            </span>
          </div>
          {isOnOffer && (
            <OfferBadge size={isMobile ? 'small' : 'normal'}/>
          )}
        </div>
      </Modal.Header>

      <Modal.Content>
        <div className={styles.historyPane}>
          {/* Product data: Area? / Price per sqm / Price total */}
          <div className={styles.dataContainer}>
            {/* Area */}
            {product.area_original && (
              <div className={styles.dataCrate}>
                <div className={styles.labelBox}>
                  <span className={styles.dataLabel}>
                    {t('common.area_m2')}
                  </span>
                </div>
                <div className={styles.valueBox}>
                  <span className={styles.dataValue}>
                    {formatProductArea(product)}
                  </span>
                </div>
              </div>
            )}
            {/* Price per sqm */}
            <div className={styles.dataCrate}>
              <div className={styles.labelBox}>
                <span className={styles.dataLabel}>
                  {t('product.price_sqm')}
                </span>
                {vatNote && (
                  <span className={styles.dataLabelAdage}>
                    {t('product.price_vat_included_note', { vat: vatNote })}
                  </span>
                )}
              </div>
              <div className={styles.valueBox}>
                <span className={styles.dataValue}>{pricePerSqm ?? totalPrice}</span>
                {isOnOffer && strikethroughPricePerSqm && (
                  <span
                    className={styles.strikethrough}
                    aria-label={t('product.strikethrough_aria')}
                  >
                    {strikethroughPricePerSqm}
                  </span>
                )}
                {isOnOffer && omnibusPricePerSqm && (
                  <span className={styles.omnibusNote}>
                    {t('product.omnibus_label')} {omnibusPricePerSqm}
                  </span>
                )}
              </div>
            </div>

            {/* Price total */}
            <div className={styles.dataCrate}>
              <div className={styles.labelBox}>
                <span className={styles.dataLabel}>
                  {getProductCategoryString('price', product.product_category)}
                </span>
                {vatNote && (
                  <span className={styles.dataLabelAdage}>
                    {t('product.price_vat_included_note', { vat: vatNote })}
                  </span>
                )}
              </div>
              <div className={styles.valueBox}>
                <span className={styles.dataValue}>{totalPrice}</span>
                {isOnOffer && strikethroughPrice && (
                  <span
                    className={styles.strikethrough}
                    aria-label={t('product.strikethrough_aria')}
                  >
                    {strikethroughPrice}
                  </span>
                )}
                {isOnOffer && omnibusPrice && (
                  <span className={styles.omnibusNote}>
                    {t('product.omnibus_label')} {omnibusPrice}
                  </span>
                )}
              </div>
            </div>          
          </div>

          <div className={styles.toggleButtonsContainer}>
            {/* Toggle buttons: Table / Chart */}
            <ButtonGroup 
              sliding
              size={togglesSize}
              style={{ 
                width: `calc(2 * ${formatTogglesSize} + 2px)`, 
                flexShrink: 0 
              }}
            >
              <Button
                type="button"
                kind='text'
                size={togglesSize}
                selected={!showChart}
                onClick={() => setShowChart(false)}
                style={{ width: formatTogglesSize }}
              >
                  {t('product.price_history_table_label')}
              </Button>
              <Button
                type="button"
                kind='text'
                size={togglesSize}
                selected={showChart}
                onClick={() => setShowChart(true)}
                style={{ width: formatTogglesSize }}
              >
                  {t('product.price_history_chart_label')}
              </Button>
            </ButtonGroup>
            {/* Toggle buttons: total / sqm */}
            {showChart && (
            <ButtonGroup 
              sliding
              size={togglesSize}
              className={styles.chartSwitcherGroup}
              style={{ 
                width: `calc(2 * ${chartTogglesSize} + 2px)`, 
                flexShrink: 0 
              }}
            >
              <Button
                type="button"
                kind='text'
                size={togglesSize}
                selected={priceKey === CHART_KEYS.PRICE_TOTAL}
                onClick={() => setPriceKey(CHART_KEYS.PRICE_TOTAL)}
                style={{ width: chartTogglesSize }}
              >
                {t('product.price_history_total')}
              </Button>
              <Button
                type="button"
                kind='text'
                size={togglesSize}
                selected={priceKey === CHART_KEYS.PRICE_M2}
                onClick={() => setPriceKey(CHART_KEYS.PRICE_M2)}
                style={{ width: chartTogglesSize }}
              >
                {t('product.price_history_sqm')}
              </Button>
            </ButtonGroup>
            )}
          </div>

          {/* Table View */}
          {!showChart && (
            <div className={styles.tableContainer}>
              <div className={styles.historyGrid} role="table" aria-label={t('product.price_history_table_label')}>
                <div className={styles.historyGridHeader} role="rowgroup">
                  <div className={styles.historyGridRow} role="row">
                    <div className={styles.historyGridHeadCell} role="columnheader">
                      {t('product.price_history_date')}
                    </div>
                    <div className={`${styles.historyGridHeadCell} ${styles.historyGridNumber}`} role="columnheader">
                      {t('product.price_history_sqm')}
                    </div>
                    <div className={`${styles.historyGridHeadCell} ${styles.historyGridNumber}`} role="columnheader">
                      {getProductCategoryString('price', product.product_category)}
                    </div>
                  </div>
                </div>

                <div className={styles.historyGridBody} role="rowgroup">
                  {tableHistory.map((entry, idx) => (
                    <div className={styles.historyGridRow} role="row" key={`${entry.date}-${idx}`}>
                      <div className={styles.historyGridCell} role="cell">{formatDate(entry.date)}</div>
                      <div className={`${styles.historyGridCell} ${styles.historyGridNumber}`} role="cell">
                        {formatCurrency(entry.price_m2_brutto, { currency, locale, digits: 0 })}
                      </div>
                      <div className={`${styles.historyGridCell} ${styles.historyGridNumber}`} role="cell">
                        {formatCurrency(entry.price_brutto, { currency, locale, digits: 0 })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Chart View */} 
          {showChart && (
            <div className={styles.chartContainer}>
              {!chartData ? (
                <div className={styles.chartPlaceholderCard}>
                  <span className={styles.placeholderText}>
                    {t('product.price_history_chart_empty')}
                  </span>
                </div>       
              ) : (
              <>
                <PriceHistoryChartSvg
                  chartData={chartData}
                  hoveredPointIndex={hoveredPointIndex}
                  onHoveredPointChange={handleHoveredPointChange}
                  formatDate={formatDate}
                  formatMoney={(value) => formatCurrency(value, { currency, locale, digits: 0 })}
                  totalLabel={t('product.price_history_total')}
                  sqmLabel={t('product.price_history_sqm')}
                  ariaLabel={t('product.price_history_title')}
                  todayLabel={t('product.today_short')}
                  styles={styles}
                />
                {normalizedHistory.length > 1 && (
                  <div className={styles.sliderCrate}>
                    {/* <span className={styles.sliderCta}>
                      {t('product.price_history_set_date_range')}
                    </span> */}
                    <div className={styles.sliderLabelRow}>
                      <span className={`${styles.sliderName}${isRangeActive ? ` ${styles.sliderLabelActive}` : ''}`}>
                        {t('product.price_history_set_date_range')}
                      </span>
                      <span className={`${styles.sliderValues}${
                        isRangeActive 
                          ? ` ${styles.sliderValuesActive}` 
                          : ''}`
                      }>
                        {formatDate(new Date(effectiveDateRange[0]).toISOString().split('T')[0])} — {formatDate(new Date(effectiveDateRange[1]).toISOString().split('T')[0])}
                      </span>
                    </div>
                    <RangeSlider
                      id="price-history-range"
                      defaultValue={dateRangeBounds}
                      value={effectiveDateRange}
                      onChange={handleDateRangeChange}
                      step={86400000}
                      formatValue={formatSliderValue}
                      isActive={isRangeActive}
                    />
                  </div>
                )}
              </>
            )}
            </div>
          )}

          {/* Disclaimer */}
          {disclaimer && <div className={styles.disclaimer}>{disclaimer}</div>}
        </div>
      </Modal.Content>
    </Modal>
  )
}